// src/App.js

import React, { useState, useMemo, useEffect, useRef } from "react";
import "./hero-styles.css";

/* ✅ Firebase Auth (Email/Password) */
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";

/* ✅ Firestore (Subscription/Trial source of truth) */
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

import {
  HEROES,
  resolveProgram,
  getExerciseVariant,
  getProgressiveSetsReps,
  getSetsCountFromPrescription,
  getSuggestedWeight,
} from "./programs";

import ProfilePanel, { defaultProfile } from "./profilepanel";
import ProgressOverview from "./progressoverview";
import Logbook from "./logbook";
import { EXERCISE_VIDEO_MAP } from "./exerciseVideos";
import ChallengesPanel from "./ChallengesPanel";

/* -----------------------------
   ✅ FIREBASE CONFIG (NO .ENV)
   ----------------------------- */

const firebaseConfig = {
  apiKey: "AIzaSyB_d6zcfbRcbhdqAxtJCkxJA48jdI0aEKs",
  authDomain: "hero-physique-trainer.firebaseapp.com",
  projectId: "hero-physique-trainer",
  storageBucket: "hero-physique-trainer.firebasestorage.app",
  messagingSenderId: "102428046118",
  appId: "1:102428046118:web:5253065c0041d9aef7c7e7",
  measurementId: "G-3XF098SLG4",
};

const isFirebaseConfigured =
  !!firebaseConfig.apiKey &&
  firebaseConfig.apiKey !== "PASTE_API_KEY_HERE" &&
  !!firebaseConfig.authDomain &&
  firebaseConfig.authDomain !== "PASTE_AUTH_DOMAIN_HERE" &&
  !!firebaseConfig.projectId &&
  firebaseConfig.projectId !== "PASTE_PROJECT_ID_HERE" &&
  !!firebaseConfig.appId &&
  firebaseConfig.appId !== "PASTE_APP_ID_HERE";

let firebaseApp = null;
let firebaseAuth = null;
let firebaseDb = null;

try {
  if (isFirebaseConfigured) {
    firebaseApp = getApps().length
      ? getApps()[0]
      : initializeApp(firebaseConfig);
    firebaseAuth = getAuth(firebaseApp);
    firebaseDb = getFirestore(firebaseApp);
  }
} catch (e) {
  firebaseApp = null;
  firebaseAuth = null;
  firebaseDb = null;
  // eslint-disable-next-line no-console
  console.error("Firebase init failed:", e);
}

/* FILTER CONSTANTS */
const UNIVERSES = ["All", "Marvel", "DC", "Anime", "Games"];
const GENDER_FILTERS = ["All", "Male", "Female"];

/* SIDEKICK / DAILY NOTIFICATION CONSTANTS */
const SIDEKICK_LAST_SEEN_KEY = "heroSidekickLastSeen_v1";
const SIDEKICK_CHECKIN_KEY = "heroSidekickCheckin_v1";
const DISCLAIMER_ACK_KEY = "heroDisclaimerAccepted_v1";

/* ✅ SUBSCRIPTION / TRIAL (Firestore-backed) */
const TRIAL_DAYS = 7;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

/* ✅ PAYWALL LINKS (Systeme.io URLs) */
const CHECKOUT_URL = "https://www.lowytradesacademy.com/hero-checkout";
const MANAGE_SUBSCRIPTION_URL = "https://www.lowytradesacademy.com/hero-manage";

/* ✅ TRIAL LIMITS */
const TRIAL_ALLOWED_HERO_COUNT = 2; // only 2 heroes selectable in trial
const TRIAL_MAX_WEEK = 1; // only week 1 accessible in trial

const openExternal = (url) => {
  if (!url) return;
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    // ignore
  }
};

/**
 * ✅ Trial logic with PAID OVERRIDE
 * - If user paid (isPro === true OR subscribed === true): they are NOT trial-limited, regardless of trial timing.
 * - Otherwise: trial counts down from trialStartISO for TRIAL_DAYS.
 */
const computeTrialInfo = (userState) => {
  if (!userState || !userState.trialStartISO) {
    return {
      hasTrial: false,
      inTrial: false,
      trialExpired: false,
      daysLeft: 0,
      trialStartISO: null,
    };
  }

  // ✅ PAID USERS OVERRIDE TRIAL COMPLETELY
  if (userState.isPro === true || userState.subscribed === true) {
    return {
      hasTrial: true,
      inTrial: false,
      trialExpired: false,
      daysLeft: 0,
      trialStartISO: userState.trialStartISO,
    };
  }

  const startMs = new Date(userState.trialStartISO).getTime();
  const nowMs = Date.now();
  const elapsedDays = Math.floor((nowMs - startMs) / MS_PER_DAY);
  const daysLeft = Math.max(0, TRIAL_DAYS - elapsedDays);
  const trialExpired = elapsedDays >= TRIAL_DAYS;

  return {
    hasTrial: true,
    inTrial: !trialExpired,
    trialExpired,
    daysLeft,
    trialStartISO: userState.trialStartISO,
  };
};

const getHeroPronouns = (gender) => {
  if (gender === "female") {
    return {
      subject: "she",
      object: "her",
      possessive: "her",
      reflexive: "herself",
    };
  }
  return {
    subject: "he",
    object: "him",
    possessive: "his",
    reflexive: "himself",
  };
};

const SIDEKICK_TEMPLATES = [
  (p) =>
    `The version of you 90 days from now is watching. Make ${p.object} proud today.`,
  () => "You don’t need motivation, you need momentum. One session. Today.",
  () => "Heroes don’t wait to feel ready. They start, then they sharpen.",
  () => "You’re one good workout away from a better mood and a better body.",
  () => "Discipline is your real superpower. Flex it today.",
  () => "Future you is silently thanking you for every rep you don’t skip.",
  () => "Small wins stacked daily become ‘impossible’ transformations.",
  () => "If your favorite hero had your day, they’d still get the work done.",
  () => "You don’t rise to the occasion, you fall to your training. Train.",
  () =>
    "Aesthetic is built in the dark. This session counts more than you think.",
];

const pickSidekickMessageForDate = (dateStr, heroGender) => {
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    hash = (hash + dateStr.charCodeAt(i) * (i + 1)) % 100000;
  }
  const idx = hash % SIDEKICK_TEMPLATES.length;
  const pronouns = getHeroPronouns(heroGender);
  const template = SIDEKICK_TEMPLATES[idx];
  return template(pronouns);
};

/* ✅ PAYWALL / LOCKED MODAL */

const PaywallModal = ({
  open,
  onClose,
  title = "Locked 🔒",
  message,
  checkoutUrl,
  manageUrl,
}) => {
  if (!open) return null;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 96,
        background: "rgba(15,23,42,0.86)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 560,
          width: "100%",
          border: "1px solid rgba(56,189,248,0.55)",
          background: "rgba(2,6,23,0.98)",
          boxShadow: "0 22px 80px rgba(15,23,42,0.95)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header">
          <div className="card-title">{title}</div>
          <div className="tag">$9.99/month</div>
        </div>

        <div className="program-desc" style={{ marginTop: 6 }}>
          {message ||
            "This feature is locked during the free trial. Unlock full access to use everything in the app."}
        </div>

        <div
          style={{
            marginTop: 10,
            fontSize: "0.78rem",
            opacity: 0.85,
            lineHeight: 1.35,
            borderTop: "1px solid rgba(148,163,184,0.18)",
            paddingTop: 10,
          }}
        >
          Billing note: Checkout is handled by <strong>Systeme.io</strong> and
          paid via <strong>PayPal</strong>. The subscription{" "}
          <strong>auto-renews</strong> until cancelled.
        </div>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => openExternal(checkoutUrl)}
            style={{
              padding: "10px 14px",
              borderRadius: 999,
              border: "1px solid rgba(56,189,248,0.85)",
              background:
                "radial-gradient(circle at 20% 0%, rgba(56,189,248,0.35), rgba(15,23,42,0.98))",
              color: "#e0f2fe",
              fontSize: "0.95rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            title="Open checkout"
          >
            Upgrade Your Plan → Checkout
          </button>

          <button
            onClick={() => openExternal(manageUrl)}
            style={{
              padding: "10px 14px",
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,0.7)",
              background: "rgba(15,23,42,0.95)",
              color: "#e5e7eb",
              fontSize: "0.95rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            title="Manage subscription"
          >
            Manage Subscription
          </button>

          <button
            onClick={onClose}
            style={{
              padding: "10px 14px",
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,0.5)",
              background: "transparent",
              color: "#e5e7eb",
              fontSize: "0.95rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Not now
          </button>
        </div>

        <div
          style={{
            marginTop: 10,
            fontSize: "0.75rem",
            opacity: 0.75,
            lineHeight: 1.35,
          }}
        >
          Tip: If you already subscribed and still see locks, use the same email
          you paid with and open “Manage Subscription”.
        </div>
      </div>
    </div>
  );
};

const UniverseTabs = ({
  selectedUniverse,
  onSelect,
  locked,
  onLockedClick,
}) => (
  <div className="pill-row">
    {UNIVERSES.map((u) => (
      <button
        key={u}
        className={u === selectedUniverse ? "pill active" : "pill"}
        onClick={() => {
          if (locked) return onLockedClick?.();
          onSelect(u);
        }}
        title={locked ? "Upgrade to unlock filters" : "Filter heroes"}
        style={locked ? { opacity: 0.65, cursor: "pointer" } : undefined}
      >
        {u}
        {locked && u !== "All" ? " 🔒" : ""}
      </button>
    ))}
  </div>
);

const GenderFilterTabs = ({
  genderFilter,
  onSelect,
  locked,
  onLockedClick,
}) => (
  <div className="pill-row" style={{ marginTop: 8 }}>
    {GENDER_FILTERS.map((g) => (
      <button
        key={g}
        className={g === genderFilter ? "pill active" : "pill"}
        onClick={() => {
          if (locked) return onLockedClick?.();
          onSelect(g);
        }}
        title={locked ? "Upgrade to unlock filters" : "Filter heroes"}
        style={locked ? { opacity: 0.65, cursor: "pointer" } : undefined}
      >
        {g}
        {locked && g !== "All" ? " 🔒" : ""}
      </button>
    ))}
  </div>
);

const HeroGrid = ({
  selectedUniverse,
  genderFilter,
  selectedHeroId,
  onSelectHero,
  onAutoGoWorkouts,
  allowedHeroIds,
  onLockedHeroClick,
}) => {
  let filtered = HEROES;

  if (selectedUniverse !== "All") {
    filtered = filtered.filter((h) => h.universe === selectedUniverse);
  }

  if (genderFilter !== "All") {
    const targetGender = genderFilter === "Male" ? "male" : "female";
    filtered = filtered.filter((h) => h.gender === targetGender);
  }

  const allowedSet =
    Array.isArray(allowedHeroIds) && allowedHeroIds.length
      ? new Set(allowedHeroIds)
      : null;

  const handleSelect = (heroId) => {
    if (allowedSet && !allowedSet.has(heroId)) {
      if (onLockedHeroClick) onLockedHeroClick(heroId);
      return;
    }

    onSelectHero(heroId);
    if (onAutoGoWorkouts) onAutoGoWorkouts();
  };

  return (
    <div className="hero-grid">
      {filtered.map((hero) => {
        const isLocked = !!(allowedSet && !allowedSet.has(hero.id));
        const isActive = hero.id === selectedHeroId;

        return (
          <div
            key={hero.id}
            className={isActive ? "hero-card active spotlight" : "hero-card"}
            onClick={() => handleSelect(hero.id)}
            style={
              isLocked
                ? {
                    opacity: 0.45,
                    filter: "grayscale(0.35)",
                    cursor: "pointer",
                    position: "relative",
                  }
                : undefined
            }
            title={
              isLocked
                ? "Locked during trial — unlock full access"
                : "Select hero"
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div className="hero-card-name">{hero.name}</div>
              {isLocked && (
                <span style={{ fontSize: "0.9rem", opacity: 0.9 }}>🔒</span>
              )}
            </div>
            <div className="hero-card-sub">{hero.physique}</div>
            <div className="hero-card-sub small">
              {hero.universe} • {hero.gender === "female" ? "♀" : "♂"}
            </div>

            {isLocked && (
              <div style={{ marginTop: 6, fontSize: "0.75rem", opacity: 0.85 }}>
                Trial access: 2 heroes only
              </div>
            )}
          </div>
        );
      })}
      {filtered.length === 0 && (
        <div className="hero-card-sub small">
          No heroes match this universe + gender filter.
        </div>
      )}
    </div>
  );
};

const DisclaimerModal = ({ open, onAccept }) => {
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 120,
        background: "rgba(2,6,23,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 560,
          width: "100%",
          border: "1px solid rgba(148,163,184,0.6)",
          background: "rgba(2,6,23,0.98)",
        }}
      >
        <div className="card-header">
          <div className="card-title">Training Disclaimer</div>
          <div className="tag">Please Read</div>
        </div>

        <div className="program-desc" style={{ fontSize: "0.85rem" }}>
          <p>
            <strong>Hero Physique Trainer</strong> provides fitness programs for
            educational and motivational purposes only.
          </p>

          <p>
            Users understand that physical training carries inherent risk. Hero
            Physique Trainer is not responsible for injuries, accidents, or
            health complications that may occur.
          </p>

          <p>
            This app does <strong>not</strong> provide medical advice. Users
            should consult a physician before beginning any fitness program and
            modify exercises to match individual ability.
          </p>

          <p>
            Heroes and characters are used strictly as{" "}
            <strong>motivation</strong>. Users acknowledge they will not
            physically become a superhero or fictional character. Results vary
            based on effort, consistency, genetics, nutrition, and recovery.
          </p>

          <p>
            By continuing, you accept full responsibility for your training
            decisions.
          </p>
        </div>

        <button
          onClick={onAccept}
          style={{
            marginTop: 12,
            width: "100%",
            padding: "10px 0",
            borderRadius: 999,
            border: "1px solid rgba(56,189,248,0.8)",
            background:
              "radial-gradient(circle at 20% 0%, rgba(56,189,248,0.35), rgba(15,23,42,0.98))",
            color: "#e0f2fe",
            fontSize: "0.95rem",
            cursor: "pointer",
          }}
        >
          I Understand & Accept
        </button>
      </div>
    </div>
  );
};

/* HERO + PROGRAM PANEL */

const HeroPanel = ({ hero, program }) => (
  <div className="card">
    <div className="card-header">
      <div className="card-title">{hero.name}</div>
      <div className="tag">{program.name}</div>
    </div>
    <div className="hero-physique">{hero.physique}</div>
    <div className="hero-meta">
      <span className="badge">{hero.universe}</span>
      <span className="badge">
        {hero.gender === "female" ? "Female Hero" : "Male Hero"}
      </span>
      <span className="badge">{program.frequency}</span>
      <span className="badge">{program.weeks}-week block</span>
    </div>
    <div className="section-label">Program focus</div>
    <div className="chips">
      {program.focus.map((f) => (
        <span key={f} className="chip">
          {f}
        </span>
      ))}
    </div>
    <div className="section-label">How to use</div>
    <div className="program-desc">
      Follow this weekly template for {program.weeks} weeks. Each week, aim to
      add a little weight, an extra rep, or cleaner form. This is training
      guidance, not medical advice—adjust to your body and recovery.
    </div>
  </div>
);

/* ✅ HERO SPOTLIGHT LANDING (Heroes Tab) */

const HeroSpotlightLanding = ({
  hero,
  program,
  weeksCount,
  daysPerWeek,
  onContinue,
  onToggleBrowse,
  browseOpen,
  onViewProgress,
  onViewChallenges,
}) => {
  const badgeText = `${weeksCount}-week • ${daysPerWeek} days/wk`;

  return (
    <div className="card spotlight-landing">
      <div className="card-header">
        <div className="card-title">
          <span style={{ marginRight: 8 }}>✨</span>
          Spotlight
        </div>
        <div className="tag">Hero Landing</div>
      </div>

      <div style={{ marginTop: 6 }}>
        <div
          style={{
            fontSize: "1.35rem",
            fontWeight: 800,
            letterSpacing: 0.2,
            lineHeight: 1.15,
          }}
        >
          {hero.name}
        </div>
        <div className="hero-physique" style={{ marginTop: 4 }}>
          {hero.physique}
        </div>
      </div>

      <div className="hero-meta" style={{ marginTop: 10 }}>
        <span className="badge">{hero.universe}</span>
        <span className="badge">
          {hero.gender === "female" ? "Female" : "Male"}
        </span>
        <span className="badge">{program.name}</span>
        <span className="badge">{program.frequency}</span>
        <span className="badge">{badgeText}</span>
      </div>

      <div className="program-desc" style={{ marginTop: 10, opacity: 0.92 }}>
        Pick your hero, then commit. Continue your program, switch heroes, or
        jump into Progress / Challenges.
      </div>

      <div className="spotlight-cta-row" style={{ marginTop: 12 }}>
        <button className="spotlight-btn primary" onClick={onContinue}>
          Continue Workouts →
        </button>

        <button className="spotlight-btn" onClick={onToggleBrowse}>
          {browseOpen ? "Hide Hero Grid" : "Change Hero"}
        </button>

        <button className="spotlight-btn" onClick={onViewProgress}>
          View Progress
        </button>

        <button className="spotlight-btn" onClick={onViewChallenges}>
          View Challenges
        </button>
      </div>
    </div>
  );
};

/* LIBRARY PANEL */

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      const id = u.searchParams.get("v");
      return `https://www.youtube.com/embed/${id}`;
    }
    if (
      u.hostname.includes("youtube.com") &&
      u.pathname.startsWith("/shorts/")
    ) {
      const id = u.pathname.split("/shorts/")[1];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "");
      return `https://www.youtube.com/embed/${id}`;
    }
  } catch {
    return null;
  }
  return null;
};

const LibraryPanel = ({ selectedExercise, onSelectExercise }) => {
  const exerciseNames = useMemo(
    () => Object.keys(EXERCISE_VIDEO_MAP).sort(),
    []
  );

  const effectiveSelected =
    selectedExercise && EXERCISE_VIDEO_MAP[selectedExercise]
      ? selectedExercise
      : exerciseNames[0];

  const videoUrl = EXERCISE_VIDEO_MAP[effectiveSelected];
  const embedUrl = getYouTubeEmbedUrl(videoUrl);

  const handleChange = (e) => {
    const name = e.target.value;
    onSelectExercise(name);
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Exercise Library</div>
        <div className="tag">How-to videos</div>
      </div>

      <p className="program-desc">
        Pick an exercise to see a quick form breakdown. This is a generic guide
        — always match to your own mobility, equipment, and warm-up.
      </p>

      <div
        className="profile-field-row"
        style={{ alignItems: "center", marginBottom: 12 }}
      >
        <div className="profile-field" style={{ width: "100%" }}>
          <label>Choose exercise</label>
          <select
            value={effectiveSelected}
            onChange={handleChange}
            style={{
              width: "100%",
              background: "rgba(15,23,42,0.95)",
              border: "1px solid rgba(148,163,184,0.6)",
              color: "#e5e7eb",
              padding: "8px 10px",
              borderRadius: 12,
              fontSize: "0.9rem",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
            }}
          >
            {exerciseNames.map((name) => (
              <option
                key={name}
                value={name}
                style={{ background: "#020617", color: "#e5e7eb" }}
              >
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {embedUrl ? (
        <div
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid rgba(148,163,184,0.4)",
            background: "black",
          }}
        >
          <div
            style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}
          >
            <iframe
              title={effectiveSelected}
              src={embedUrl}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      ) : (
        <p className="program-desc">
          No video available for this exercise yet. Pick another movement from
          the list.
        </p>
      )}

      <div className="footer-note" style={{ marginTop: 10 }}>
        These are general demonstrations. Use a weight you can control, respect
        your joints, and adjust as needed.
      </div>
    </div>
  );
};

/* PROGRAM PANEL */

const ProgramPanel = ({
  program,
  daysPerWeek,
  selectedDayId,
  onSelectDay,
  progressForWeek,
  onToggleDay,
  selectedWeek,
  difficulty,
  logForWeek,
  prevWeekLog,
  onUpdateLog,
  onShowVideo,
}) => {
  const [openExercises, setOpenExercises] = useState({});

  const allDays = program.days || [];
  const visibleDays = allDays.slice(0, daysPerWeek);

  if (!visibleDays.length) return null;

  const activeDay =
    visibleDays.find((d) => d.id === selectedDayId) || visibleDays[0];

  const completedCount = visibleDays.filter(
    (d) => progressForWeek && progressForWeek[d.id]
  ).length;

  const dayLog = (logForWeek && logForWeek[activeDay.id]) || {};

  const toggleExerciseOpen = (exerciseName) => {
    setOpenExercises((prev) => ({
      ...prev,
      [exerciseName]: !prev[exerciseName],
    }));
  };

  const isAdvanced = difficulty === "advanced";

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Weekly Training Days</div>
        <div className="tag">
          {daysPerWeek} day{daysPerWeek > 1 ? "s" : ""}/week
        </div>
      </div>

      <div className="section-label">Choose a day</div>
      <div className="day-tabs">
        {visibleDays.map((day) => (
          <button
            key={day.id}
            className={day.id === activeDay.id ? "day-tab active" : "day-tab"}
            onClick={() => onSelectDay(day.id)}
          >
            {day.label}
          </button>
        ))}
      </div>

      <div className="progress-row">
        <span className="progress-summary">
          This week: {completedCount}/{visibleDays.length} days completed
        </span>
        <button
          className={
            progressForWeek && progressForWeek[activeDay.id]
              ? "progress-toggle completed"
              : "progress-toggle"
          }
          onClick={() => onToggleDay(activeDay.id)}
        >
          {progressForWeek && progressForWeek[activeDay.id]
            ? "Mark as not done"
            : "Mark this day done"}
        </button>
      </div>

      <div className="progress-pills">
        {visibleDays.map((day) => {
          const done = !!(progressForWeek && progressForWeek[day.id]);
          return (
            <button
              key={day.id}
              className={done ? "progress-pill done" : "progress-pill"}
              onClick={() => onToggleDay(day.id)}
            >
              <span className="dot">{done ? "●" : "○"}</span>
              <span className="label">{day.label.replace("Day ", "D")}</span>
            </button>
          );
        })}
      </div>

      <div className="day-info">
        Week {selectedWeek} · Difficulty:{" "}
        {difficulty === "beginner"
          ? "Beginner"
          : difficulty === "advanced"
          ? "Advanced"
          : "Intermediate"}
        . Volume and exercise demands ramp up across the block. Don’t chase
        failure—chase progression with solid form.
      </div>

      <div className="exercise-list">
        {activeDay.exercises.map((ex) => {
          const variant = getExerciseVariant(ex, selectedWeek, difficulty);

          const entryRaw = dayLog[ex.name] || {};
          const entry = {
            rpe: entryRaw.rpe || "",
            sets: Array.isArray(entryRaw.sets) ? entryRaw.sets : [],
          };

          const isOpen = !!openExercises[ex.name];
          const setsCount = getSetsCountFromPrescription(ex.setsReps);

          const suggestion =
            selectedWeek > 1
              ? getSuggestedWeight(
                  prevWeekLog,
                  activeDay.id,
                  ex.name,
                  ex.setsReps,
                  difficulty
                )
              : null;

          const hasVideo = !!EXERCISE_VIDEO_MAP[ex.name];

          return (
            <div key={ex.name} className="exercise">
              <div
                className="exercise-main"
                style={{ alignItems: "flex-start", gap: 8 }}
              >
                <div>
                  <div className="exercise-name">{variant.name}</div>
                  <div className="exercise-sets">
                    {getProgressiveSetsReps(
                      ex.setsReps,
                      selectedWeek,
                      difficulty
                    )}
                  </div>

                  {suggestion && (
                    <div
                      className="exercise-suggestion"
                      style={{
                        fontSize: "0.8rem",
                        opacity: 0.85,
                        marginTop: 2,
                      }}
                    >
                      Suggested next week:{" "}
                      <strong>{suggestion.weight} lbs</strong>{" "}
                      <span style={{ opacity: 0.8 }}>
                        {suggestion.delta > 0
                          ? `( +${suggestion.delta.toFixed(1)} )`
                          : suggestion.delta < 0
                          ? `( ${suggestion.delta.toFixed(1)} )`
                          : `(maintain)`}
                      </span>
                    </div>
                  )}

                  {hasVideo && (
                    <button
                      onClick={() => onShowVideo(ex.name)}
                      style={{
                        marginTop: 4,
                        fontSize: "0.8rem",
                        padding: "2px 6px",
                        borderRadius: 999,
                        border: "1px solid rgba(148,163,184,0.6)",
                        background: "rgba(15,23,42,0.9)",
                        color: "#e5e7eb",
                        cursor: "pointer",
                      }}
                    >
                      🎥 Watch how-to video
                    </button>
                  )}
                </div>

                <button
                  onClick={() => toggleExerciseOpen(ex.name)}
                  style={{
                    marginLeft: "auto",
                    fontSize: "0.8rem",
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: "1px solid var(--border-color, #333)",
                    background: isOpen
                      ? "var(--accent-bg, #222)"
                      : "transparent",
                    cursor: "pointer",
                  }}
                >
                  {isOpen ? "▼ Hide log" : "▶ Log sets"}
                </button>
              </div>

              <div className="exercise-notes">{variant.notes}</div>

              {isOpen && (
                <div
                  className="sets-log"
                  style={{
                    marginTop: 8,
                    padding: 8,
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      opacity: 0.8,
                      marginBottom: 6,
                    }}
                  >
                    Log weights & reps
                    {isAdvanced ? " + RPE (advanced only)" : ""} for each set:
                  </div>

                  {suggestion && (
                    <button
                      style={{
                        fontSize: "0.75rem",
                        padding: "3px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.2)",
                        background: "rgba(255,255,255,0.05)",
                        marginBottom: 6,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        for (let i = 0; i < setsCount; i++) {
                          onUpdateLog(
                            activeDay.id,
                            ex.name,
                            "setWeight",
                            String(suggestion.weight),
                            i
                          );
                        }
                      }}
                    >
                      Use suggested weight for all sets
                    </button>
                  )}

                  {Array.from({ length: setsCount }).map((_, idx) => {
                    const setEntry = entry.sets[idx] || {
                      weight: "",
                      reps: "",
                      rpe: "",
                    };
                    return (
                      <div
                        key={idx}
                        className="set-row"
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          marginBottom: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: "0.8rem", minWidth: 40 }}>
                          Set {idx + 1}
                        </span>

                        <input
                          style={{
                            padding: "4px 6px",
                            fontSize: "0.8rem",
                            flex: "1 1 90px",
                            minWidth: 0,
                          }}
                          placeholder="Weight"
                          value={setEntry.weight}
                          onChange={(e) =>
                            onUpdateLog(
                              activeDay.id,
                              ex.name,
                              "setWeight",
                              e.target.value,
                              idx
                            )
                          }
                        />

                        <input
                          style={{
                            padding: "4px 6px",
                            fontSize: "0.8rem",
                            flex: "1 1 70px",
                            minWidth: 0,
                          }}
                          placeholder="Reps"
                          value={setEntry.reps}
                          onChange={(e) =>
                            onUpdateLog(
                              activeDay.id,
                              ex.name,
                              "setReps",
                              e.target.value,
                              idx
                            )
                          }
                        />

                        {isAdvanced && (
                          <input
                            style={{
                              padding: "4px 6px",
                              fontSize: "0.8rem",
                              flex: "1 1 70px",
                              minWidth: 0,
                            }}
                            placeholder="RPE"
                            value={setEntry.rpe}
                            onChange={(e) =>
                              onUpdateLog(
                                activeDay.id,
                                ex.name,
                                "setRpe",
                                e.target.value,
                                idx
                              )
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="footer-note">
        Always warm up 5–10 minutes. Use weights you can control with good form.
        Adjust volume and exercise selection to your joints, equipment, and
        recovery.
      </div>
    </div>
  );
};

/* RADIAL NAV TOGGLE – 7 tabs on full 360° with 360/7 spacing */

const NavToggle = ({ activeTab, onChange }) => {
  const [open, setOpen] = useState(false);

  const baseTabs = [
    { id: "profile", label: "Profile", icon: "👤" },
    { id: "heroes", label: "Heroes", icon: "⚡" },
    { id: "workouts", label: "Workouts", icon: "📅" },
    { id: "library", label: "Library", icon: "🎥" },
    { id: "progress", label: "Progress", icon: "📈" },
    { id: "logbook", label: "Logbook", icon: "📓" },
    { id: "challenges", label: "Challenges", icon: "🏆" },
  ];

  const total = baseTabs.length;
  const step = total > 0 ? 360 / total : 0;
  const startAngle = -90;

  const tabs = baseTabs.map((tab, index) => ({
    ...tab,
    angleDeg: startAngle + index * step,
  }));

  const radius = 115;
  const wheelSize = radius * 2 + 80;

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 65,
        pointerEvents: "none",
      }}
    >
      {open && (
        <div
          style={{
            position: "relative",
            width: wheelSize,
            height: wheelSize,
            pointerEvents: "auto",
          }}
        >
          <button
            onClick={() => setOpen(false)}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 64,
              height: 64,
              borderRadius: 999,
              border: "2px solid rgba(94,234,212,0.9)",
              background:
                "radial-gradient(circle at 30% 0%, #22d3ee 0, #0f172a 55%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 10px 35px rgba(15,23,42,0.95)",
            }}
            title="Navigation"
          >
            <span style={{ fontSize: "1.6rem" }}>⚡</span>
          </button>

          {tabs.map((tab) => {
            const angleRad = (tab.angleDeg * Math.PI) / 180;
            const x = radius * Math.cos(angleRad);
            const y = radius * Math.sin(angleRad);
            const isActive = tab.id === activeTab;

            return (
              <button
                key={tab.id}
                onClick={() => handleSelect(tab.id)}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: isActive
                    ? "1px solid rgba(56,189,248,0.9)"
                    : "1px solid rgba(148,163,184,0.6)",
                  background: isActive
                    ? "radial-gradient(circle at 30% 0%, rgba(56,189,248,0.35) 0, rgba(15,23,42,0.96) 55%)"
                    : "rgba(15,23,42,0.96)",
                  color: "#e5e7eb",
                  fontSize: "0.8rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 8px 25px rgba(15,23,42,0.9)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {isActive && (
                  <span style={{ fontSize: "0.6rem", opacity: 0.9 }}>●</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            pointerEvents: "auto",
            width: 56,
            height: 56,
            borderRadius: 999,
            border: "1px solid rgba(148,163,184,0.7)",
            background: "rgba(15,23,42,0.96)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 8px 30px rgba(15,23,42,0.9)",
          }}
          title="Navigation"
        >
          <span style={{ fontSize: "1.4rem" }}>⚡</span>
        </button>
      )}
    </div>
  );
};

/* ✅ AUTH COVER – Firebase Email/Password */

const AuthCover = ({ onLogin, onSignup, firebaseReady }) => {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const em = email.trim();
    if (!em || !password) {
      setError("Please enter both email and password.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!firebaseReady) {
      setError(
        "Firebase isn't configured yet. Paste your Firebase Web App config into firebaseConfig at the top of App.js, save, and refresh."
      );
      return;
    }

    setBusy(true);
    try {
      if (mode === "login") {
        await onLogin(em, password);
      } else {
        await onSignup(em, password);
      }
    } catch (err) {
      const msg =
        (err && err.code && String(err.code)) ||
        (err && err.message) ||
        "Auth error";
      if (
        msg.includes("auth/invalid-credential") ||
        msg.includes("auth/wrong-password")
      ) {
        setError("Incorrect email or password.");
      } else if (msg.includes("auth/email-already-in-use")) {
        setError("That email is already in use. Try logging in.");
      } else if (msg.includes("auth/invalid-email")) {
        setError("That email format looks invalid.");
      } else if (msg.includes("auth/weak-password")) {
        setError("Password too weak. Use at least 6 characters.");
      } else if (msg.includes("auth/api-key-not-valid")) {
        setError(
          "Your Firebase API key is invalid/missing. Paste the correct firebaseConfig from Firebase Console (Project Settings → Web app)."
        );
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cover-overlay">
      <div className="card cover-card" style={{ maxWidth: 520 }}>
        <div className="card-header">
          <div className="card-title">Hero Physique Trainer</div>
          <div className="tag">9–12 Week Hero Programs</div>
        </div>

        <p className="program-desc">
          <strong>Every hero has an origin.</strong>
          <br />
          <strong>This is yours.</strong>
          <br />
          <br />
          Pick a hero and commit to their 9 or 12-week program — built for real
          progression, real strength, and real transformation.
          <br />
          Train 4–5 days per week with Hero Physique Trainer for maximum growth.
          <br />
          <br />
          Track your streaks, review your progress, & watch your physique
          evolve.
        </p>

        <div className="cover-hero-strip">
          <div className="hero-orb hero-orb-1">
            <span>🛡️</span>
            <div className="hero-orb-label">Super Soldier</div>
          </div>
          <div className="hero-orb hero-orb-2">
            <span>⚡</span>
            <div className="hero-orb-label">Powerhouse</div>
          </div>
          <div className="hero-orb hero-orb-3">
            <span>🕷️</span>
            <div className="hero-orb-label">Agile Hero</div>
          </div>
        </div>

        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 16,
            background: "rgba(15,23,42,0.95)",
            border: "1px solid rgba(148,163,184,0.6)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: "0.85rem", opacity: 0.9 }}>
              Email-only login. Create an account to start your 7-day free
              trial.
            </div>

            <div
              style={{
                display: "inline-flex",
                padding: 3,
                borderRadius: 999,
                background: "rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.6)",
              }}
            >
              <button
                type="button"
                onClick={() => setMode("login")}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "none",
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  background:
                    mode === "login" ? "rgba(56,189,248,0.3)" : "transparent",
                  color: "#e5e7eb",
                }}
              >
                Log in
              </button>
              <button
                type="button"
                onClick={() => setMode("signup")}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "none",
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  background:
                    mode === "signup" ? "rgba(56,189,248,0.3)" : "transparent",
                  color: "#e5e7eb",
                }}
              >
                Sign up
              </button>
            </div>
          </div>

          {!firebaseReady && (
            <div
              style={{
                marginBottom: 10,
                fontSize: "0.8rem",
                color: "#fde68a",
                background: "rgba(120,53,15,0.45)",
                borderRadius: 10,
                padding: 10,
                border: "1px solid rgba(245,158,11,0.35)",
              }}
            >
              Firebase is not configured yet. Paste your Firebase Web App config
              into <strong>firebaseConfig</strong> at the top of App.js and
              refresh.
            </div>
          )}

          <form onSubmit={handleSubmit} className="profile-field-row">
            <div className="profile-field">
              <label>Email</label>
              <input
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@hero.com"
                autoComplete="email"
                style={{
                  background: "rgba(15,23,42,0.98)",
                  border: "1px solid rgba(148,163,184,0.7)",
                  color: "#e5e7eb",
                  borderRadius: 12,
                  padding: "8px 10px",
                  fontSize: "0.9rem",
                  marginTop: 2,
                }}
              />
            </div>

            <div className="profile-field">
              <label>Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete={
                  mode === "signup" ? "new-password" : "current-password"
                }
                style={{
                  background: "rgba(15,23,42,0.98)",
                  border: "1px solid rgba(148,163,184,0.7)",
                  color: "#e5e7eb",
                  borderRadius: 12,
                  padding: "8px 10px",
                  fontSize: "0.9rem",
                }}
              />
            </div>

            {error && (
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#fecaca",
                  background: "rgba(127,29,29,0.6)",
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              style={{
                marginTop: 6,
                width: "100%",
                padding: "10px 0",
                borderRadius: 999,
                border: "1px solid rgba(56,189,248,0.8)",
                background:
                  "radial-gradient(circle at 20% 0%, rgba(56,189,248,0.35), rgba(15,23,42,0.98))",
                color: "#e0f2fe",
                fontSize: "0.95rem",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy
                ? "Please wait…"
                : mode === "login"
                ? "Log in and train"
                : "Create account and start 7-day free trial"}
            </button>
          </form>

          <div
            style={{
              marginTop: 8,
              fontSize: "0.75rem",
              opacity: 0.7,
              textAlign: "center",
              lineHeight: 1.35,
            }}
          >
            Billing after trial is handled on your checkout page via{" "}
            <strong>PayPal</strong> (Systeme.io) and auto-renews until
            cancelled.
          </div>
        </div>
      </div>
    </div>
  );
};

/* SETTINGS PANEL MODAL */

const SettingsPanel = ({
  open,
  onClose,
  authId,
  onLogout,
  subscriptionLabel,
  manageSubscriptionUrl,
  checkoutUrl,
  hasFullAccess,
}) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const upgradeLabel = hasFullAccess
    ? "You are currently on full version ✅"
    : "Upgrade Your Plan";

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(15,23,42,0.8)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "10vh",
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 460,
          width: "100%",
          position: "relative",
          border: "1px solid rgba(148,163,184,0.8)",
          background: "rgba(2,6,23,0.98)",
          boxShadow: "0 20px 60px rgba(15,23,42,0.95)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header">
          <div className="card-title">Settings</div>
          <div className="tag">Account • Preferences • App</div>
        </div>

        <div className="section-label">Account</div>
        <div
          style={{
            borderRadius: 12,
            padding: 10,
            marginBottom: 10,
            background: "rgba(15,23,42,0.95)",
            border: "1px solid rgba(148,163,184,0.6)",
            fontSize: "0.85rem",
          }}
        >
          <div style={{ marginBottom: 8 }}>
            Signed in as:
            <br />
            <strong>{authId}</strong>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
              paddingTop: 8,
              borderTop: "1px solid rgba(148,163,184,0.25)",
            }}
          >
            <div style={{ opacity: 0.9 }}>Subscription</div>
            <div
              style={{
                fontSize: "0.8rem",
                padding: "3px 10px",
                borderRadius: 999,
                border: "1px solid rgba(56,189,248,0.55)",
                background: "rgba(56,189,248,0.12)",
                color: "#e0f2fe",
                whiteSpace: "nowrap",
              }}
            >
              {subscriptionLabel}
            </div>
          </div>

          <div
            style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}
          >
            <button
              onClick={() => {
                if (hasFullAccess) return;
                openExternal(checkoutUrl);
              }}
              disabled={hasFullAccess}
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                border: hasFullAccess
                  ? "1px solid rgba(34,197,94,0.55)"
                  : "1px solid rgba(56,189,248,0.8)",
                background: hasFullAccess
                  ? "rgba(34,197,94,0.12)"
                  : "radial-gradient(circle at 20% 0%, rgba(56,189,248,0.35), rgba(15,23,42,0.98))",
                color: hasFullAccess ? "#bbf7d0" : "#e0f2fe",
                fontSize: "0.9rem",
                cursor: hasFullAccess ? "not-allowed" : "pointer",
                opacity: hasFullAccess ? 0.95 : 1,
                width: "100%",
              }}
              title={
                hasFullAccess
                  ? "You already have full access."
                  : "Open checkout"
              }
            >
              {upgradeLabel}
            </button>

            <button
              onClick={() => openExternal(manageSubscriptionUrl)}
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.7)",
                background: "rgba(15,23,42,0.95)",
                color: "#e5e7eb",
                fontSize: "0.9rem",
                cursor: "pointer",
                width: "100%",
              }}
              title="Open subscription management"
            >
              Manage Subscription
            </button>
          </div>

          <div style={{ opacity: 0.75, fontSize: "0.8rem", marginTop: 8 }}>
            Billing is processed by Systeme.io via PayPal. Subscription
            auto-renews until cancelled.
          </div>
        </div>

        <div className="section-label">Planned Features</div>
        <div
          style={{
            borderRadius: 12,
            padding: 10,
            marginBottom: 10,
            background: "rgba(15,23,42,0.95)",
            border: "1px solid rgba(56,189,248,0.6)",
            fontSize: "0.85rem",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div>• Cloud sync for your logbook and measurements</div>
          <div>• Subscription verification from Systeme.io</div>
          <div>• Push notifications for workouts & check-ins</div>
        </div>

        <div
          style={{
            marginTop: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>
            App version: <strong>0.9.3 (Beta)</strong>
            <br />
            Hero Physique Trainer
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={onLogout}
              style={{
                padding: "5px 10px",
                borderRadius: 999,
                border: "1px solid rgba(248,113,113,0.7)",
                background: "rgba(127,29,29,0.8)",
                color: "#fee2e2",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              Logout
            </button>
            <button
              onClick={onClose}
              style={{
                padding: "5px 10px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.7)",
                background: "rgba(15,23,42,0.95)",
                color: "#e5e7eb",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ✅ APP ROOT */

const App = () => {
  const [fbUser, setFbUser] = useState(null);
  const [fbReady, setFbReady] = useState(!!firebaseAuth);

  /* ================================
   DISCLAIMER STATE (Step 3)
================================ */
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  useEffect(() => {
    try {
      const accepted = window.localStorage.getItem(DISCLAIMER_ACK_KEY);
      if (!accepted) {
        setDisclaimerOpen(true);
      }
    } catch {
      setDisclaimerOpen(true);
    }
  }, []);

  const authId = useMemo(() => {
    if (!fbUser) return null;
    return fbUser.email || fbUser.uid;
  }, [fbUser]);

  const uid = useMemo(() => {
    if (!fbUser) return null;
    return fbUser.uid;
  }, [fbUser]);

  useEffect(() => {
    if (!firebaseAuth) {
      setFbReady(false);
      setFbUser(null);
      return;
    }
    setFbReady(true);
    const unsub = onAuthStateChanged(firebaseAuth, (user) => {
      setFbUser(user || null);
    });
    return () => unsub();
  }, []);

  const ensureUserDoc = async (userUid, email) => {
    if (!firebaseDb || !userUid || !email) return;

    const normalizedEmail = String(email).trim().toLowerCase();
    const ref = doc(firebaseDb, "users", userUid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      await setDoc(
        ref,
        {
          email: normalizedEmail,
          trialStartISO: new Date().toISOString(),
          subscribed: false,
          subscriptionStartISO: null,
          subscriptionSource: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else {
      const data = snap.data() || {};
      if (
        !data.email ||
        String(data.email).trim().toLowerCase() !== normalizedEmail
      ) {
        await setDoc(
          ref,
          { email: normalizedEmail, updatedAt: serverTimestamp() },
          { merge: true }
        );
      }
    }
  };

  const loginWithEmail = async (email, password) => {
    if (!firebaseAuth) throw new Error("Firebase auth not ready");

    const cred = await signInWithEmailAndPassword(
      firebaseAuth,
      email,
      password
    );

    const userUid = cred.user?.uid;
    const normalizedEmail =
      cred.user?.email?.trim().toLowerCase() ||
      String(email || "")
        .trim()
        .toLowerCase();

    if (userUid && normalizedEmail) {
      await ensureUserDoc(userUid, normalizedEmail);
    }
  };

  const signupWithEmail = async (email, password) => {
    if (!firebaseAuth) throw new Error("Firebase auth not ready");

    const cred = await createUserWithEmailAndPassword(
      firebaseAuth,
      email,
      password
    );

    const userUid = cred.user?.uid;
    const normalizedEmail =
      cred.user?.email?.trim().toLowerCase() ||
      String(email || "")
        .trim()
        .toLowerCase();

    if (userUid && normalizedEmail) {
      await setDoc(
        doc(firebaseDb, "users", userUid),
        {
          email: normalizedEmail,
          trialStartISO: new Date().toISOString(),
          subscribed: false,
          subscriptionStartISO: null,
          subscriptionSource: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  };

  const handleLogout = async () => {
    try {
      if (firebaseAuth) await signOut(firebaseAuth);
    } catch {}
  };

  // ✅ Firestore subscription state
  const [subDoc, setSubDoc] = useState(null);
  const [subDocLoading, setSubDocLoading] = useState(false);

  useEffect(() => {
    if (!firebaseDb || !uid) {
      setSubDoc(null);
      setSubDocLoading(false);
      return;
    }

    setSubDocLoading(true);

    const ref = doc(firebaseDb, "users", uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setSubDoc(snap.exists() ? snap.data() : null);
        setSubDocLoading(false);
      },
      () => {
        setSubDoc(null);
        setSubDocLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  // ✅ Trial Meta (derived)
  const [subscriptionLabel, setSubscriptionLabel] = useState("Loading…");
  const [trialMeta, setTrialMeta] = useState({
    inTrial: false,
    trialExpired: false,
    daysLeft: 0,
    hasTrial: false,
    subscribed: false,
  });

  const hasFullAccess = useMemo(() => {
    return !!(subDoc && (subDoc.isPro === true || subDoc.subscribed === true));
  }, [subDoc]);

  useEffect(() => {
    if (!uid) return;

    if (subDocLoading) {
      setSubscriptionLabel("Loading…");
      return;
    }

    if (!subDoc) {
      setSubscriptionLabel("Trial not started");
      setTrialMeta({
        inTrial: false,
        trialExpired: false,
        daysLeft: 0,
        hasTrial: false,
        subscribed: false,
      });
      return;
    }

    const trialInfo = computeTrialInfo(subDoc);

    setTrialMeta({
      inTrial: trialInfo.inTrial,
      trialExpired: trialInfo.trialExpired,
      daysLeft: trialInfo.daysLeft,
      hasTrial: trialInfo.hasTrial,
      subscribed: hasFullAccess,
    });

    if (hasFullAccess) {
      setSubscriptionLabel("$9.99/mo active");
    } else if (trialInfo.inTrial) {
      setSubscriptionLabel(`Free trial • ${trialInfo.daysLeft} day(s) left`);
    } else if (trialInfo.trialExpired) {
      setSubscriptionLabel("Trial ended • Subscribe to unlock");
    } else {
      setSubscriptionLabel("Not subscribed");
    }
  }, [uid, subDoc, subDocLoading, hasFullAccess]);

  // ✅ Paywall state
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallTitle, setPaywallTitle] = useState("Locked 🔒");
  const [paywallMessage, setPaywallMessage] = useState("");

  const openPaywall = (title, message) => {
    setPaywallTitle(title || "Locked 🔒");
    setPaywallMessage(message || "");
    setPaywallOpen(true);
  };

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [selectedUniverse, setSelectedUniverse] = useState("All");
  const [genderFilter, setGenderFilter] = useState("All");
  const [selectedHeroId, setSelectedHeroId] = useState(HEROES[0].id);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [activeTab, setActiveTab] = useState("heroes");

  const [selectedExerciseForVideo, setSelectedExerciseForVideo] =
    useState(null);

  /* ✅ GLOBAL "GO BACK" (tab history) */
  const historyRef = useRef([]);
  const [tabHistory, setTabHistory] = useState([]);
  const activeTabRef = useRef(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const navigateToTab = (tabId) => {
    if (!tabId) return;
    if (tabId === activeTabRef.current) return;

    const current = activeTabRef.current;
    historyRef.current = [...historyRef.current, current];
    setTabHistory(historyRef.current);
    setActiveTab(tabId);
  };

  const goBack = () => {
    const hist = historyRef.current || [];
    if (!hist.length) return;

    const prevTab = hist[hist.length - 1];
    historyRef.current = hist.slice(0, -1);
    setTabHistory(historyRef.current);
    setActiveTab(prevTab);
  };

  const [progress, setProgress] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("heroProgress_v3");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [daysPerWeekByHero, setDaysPerWeekByHero] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("heroDaysPerWeek_v2");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [difficultyByHero, setDifficultyByHero] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("heroDifficulty_v1");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [blockLengthByHero, setBlockLengthByHero] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("heroBlockLength_v1");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [log, setLog] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("heroLog_v1");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [profile, setProfile] = useState(() => {
    if (typeof window === "undefined") return defaultProfile;
    try {
      const raw = window.localStorage.getItem("heroProfile_v1");
      return raw ? { ...defaultProfile, ...JSON.parse(raw) } : defaultProfile;
    } catch {
      return defaultProfile;
    }
  });

  const [profilePhotos, setProfilePhotos] = useState([]);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [sidekickMessage, setSidekickMessage] = useState(null);
  const [sidekickOpen, setSidekickOpen] = useState(false);
  const [sidekickAnswered, setSidekickAnswered] = useState(false);

  // ✅ Heroes tab spotlight mode state (NEW)
  const [heroBrowseOpen, setHeroBrowseOpen] = useState(false);
  const heroBrowseTopRef = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem("heroProgress_v3", JSON.stringify(progress));
    } catch {}
  }, [progress]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        "heroDaysPerWeek_v2",
        JSON.stringify(daysPerWeekByHero)
      );
    } catch {}
  }, [daysPerWeekByHero]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        "heroDifficulty_v1",
        JSON.stringify(difficultyByHero)
      );
    } catch {}
  }, [difficultyByHero]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        "heroBlockLength_v1",
        JSON.stringify(blockLengthByHero)
      );
    } catch {}
  }, [blockLengthByHero]);

  useEffect(() => {
    try {
      window.localStorage.setItem("heroLog_v1", JSON.stringify(log));
    } catch {}
  }, [log]);

  useEffect(() => {
    try {
      window.localStorage.setItem("heroProfile_v1", JSON.stringify(profile));
    } catch {}
  }, [profile]);

  const selectedHero = useMemo(
    () => HEROES.find((h) => h.id === selectedHeroId) || HEROES[0],
    [selectedHeroId]
  );

  const program = useMemo(
    () => resolveProgram(selectedHero.programType),
    [selectedHero.programType]
  );

  const allDays = program.days || [];
  const daysPerWeek =
    daysPerWeekByHero[selectedHero.id] ||
    program.defaultDaysPerWeek ||
    Math.min(5, allDays.length);

  const defaultWeeksCount = program.weeks || 9;
  const heroBlock = blockLengthByHero[selectedHero.id];
  const weeksCount =
    heroBlock && [9, 12].includes(heroBlock) ? heroBlock : defaultWeeksCount;

  const heroProgress = progress[selectedHero.id] || {};
  const progressForHeroWeek = heroProgress[selectedWeek] || {};

  const heroLogAllWeeks = log[selectedHero.id] || {};
  const logForHeroWeek = heroLogAllWeeks[selectedWeek] || {};
  const prevWeekLog =
    selectedWeek > 1 ? heroLogAllWeeks[selectedWeek - 1] || {} : {};

  const difficulty = difficultyByHero[selectedHero.id] || "intermediate";

  const trialAllowedHeroIds = useMemo(() => {
    const trialMaleHero = HEROES.find((h) => h.gender === "male");
    const wonderWoman = HEROES.find(
      (h) =>
        h.name.toLowerCase().includes("black widow") ||
        h.id.toLowerCase().includes("black widow")
    );

    const ids = [];
    if (trialMaleHero) ids.push(trialMaleHero.id);
    if (wonderWoman) ids.push(wonderWoman.id);

    return ids.slice(0, TRIAL_ALLOWED_HERO_COUNT);
  }, []);

  const isTrialLimited = trialMeta.inTrial && !trialMeta.subscribed;

  useEffect(() => {
    if (!isTrialLimited) return;
    if (selectedWeek > TRIAL_MAX_WEEK) setSelectedWeek(TRIAL_MAX_WEEK);
  }, [isTrialLimited, selectedWeek]);

  useEffect(() => {
    if (!isTrialLimited) return;
    if (!trialAllowedHeroIds.includes(selectedHeroId)) {
      setSelectedHeroId(trialAllowedHeroIds[0] || HEROES[0].id);
      setSelectedDayId(null);
      setSelectedWeek(1);
      setHeroBrowseOpen(false);
    }
  }, [isTrialLimited, selectedHeroId, trialAllowedHeroIds]);

  // SIDEKICK EFFECT
  useEffect(() => {
    const heroGender = selectedHero.gender || "male";
    const baseMsg = pickSidekickMessageForDate(todayStr, heroGender);

    let measurementDue = false;
    try {
      const history = Array.isArray(profile.measurementHistory)
        ? profile.measurementHistory
        : [];
      if (!history.length) {
        measurementDue = true;
      } else {
        const last = history[history.length - 1];
        if (last && last.date) {
          const lastDate = new Date(last.date);
          const todayDate = new Date(todayStr);
          const diffMs = todayDate - lastDate;
          const diffDays = diffMs / (1000 * 60 * 60 * 24);
          if (diffDays >= 13.5) measurementDue = true;
        }
      }
    } catch {}

    const msg = measurementDue
      ? `${baseMsg} Also: it's checkpoint time. Open Profile → Measurements and save your latest stats so we can track your hero arc.`
      : baseMsg;

    setSidekickMessage(msg);

    try {
      const lastSeenRaw = window.localStorage.getItem(SIDEKICK_LAST_SEEN_KEY);
      const lastSeen = lastSeenRaw || null;

      const checkinRaw = window.localStorage.getItem(SIDEKICK_CHECKIN_KEY);
      const checkins = checkinRaw ? JSON.parse(checkinRaw) : {};
      setSidekickAnswered(!!checkins[todayStr]);

      const shouldOpen = lastSeen !== todayStr;
      setSidekickOpen(shouldOpen);

      if (shouldOpen) {
        window.localStorage.setItem(SIDEKICK_LAST_SEEN_KEY, todayStr);
      }
    } catch {
      setSidekickOpen(true);
    }
  }, [
    todayStr,
    selectedHero.gender,
    profile.measurementHistory,
    selectedHero,
    profile,
  ]);

  // Ensure selected hero fits current filters
  useEffect(() => {
    const candidates = HEROES.filter((h) => {
      const universeOk =
        selectedUniverse === "All" || h.universe === selectedUniverse;
      const genderOk =
        genderFilter === "All" ||
        h.gender === (genderFilter === "Male" ? "male" : "female");
      return universeOk && genderOk;
    });

    if (!candidates.length) return;

    if (!candidates.some((h) => h.id === selectedHeroId)) {
      setSelectedHeroId(candidates[0].id);
      setSelectedDayId(null);
      setSelectedWeek(1);
      setHeroBrowseOpen(false);
    }
  }, [selectedUniverse, genderFilter, selectedHeroId]);

  const handleUniverseChange = (universe) => setSelectedUniverse(universe);

  const handleHeroChange = (heroId) => {
    setSelectedHeroId(heroId);
    setSelectedDayId(null);
    setSelectedWeek(1);
  };

  // ✅ Spotlight hero change: select hero then collapse grid (NEW)
  const handleHeroChangeSpotlight = (heroId) => {
    handleHeroChange(heroId);
    setHeroBrowseOpen(false);
  };

  const handleDayChange = (dayId) => setSelectedDayId(dayId);

  const handleToggleDay = (dayId) => {
    setProgress((prev) => {
      const heroProg = prev[selectedHero.id] || {};
      const weekProg = heroProg[selectedWeek] || {};
      const newWeekProg = { ...weekProg, [dayId]: !weekProg[dayId] };
      return {
        ...prev,
        [selectedHero.id]: { ...heroProg, [selectedWeek]: newWeekProg },
      };
    });
  };

  const handleDaysPerWeekChange = (value) => {
    setDaysPerWeekByHero((prev) => ({ ...prev, [selectedHero.id]: value }));
  };

  const handleDifficultyChange = (mode) => {
    setDifficultyByHero((prev) => ({ ...prev, [selectedHero.id]: mode }));
  };

  const handleBlockLengthChange = (value) => {
    setBlockLengthByHero((prev) => ({ ...prev, [selectedHero.id]: value }));
    setSelectedWeek((prevWeek) => (prevWeek > value ? value : prevWeek));
  };

  const handleUpdateLog = (dayId, exerciseName, field, value, setIndex = 0) => {
    setLog((prev) => {
      const heroLog = prev[selectedHero.id] || {};
      const weekLog = heroLog[selectedWeek] || {};
      const dayLog = weekLog[dayId] || {};
      const existingEntry = dayLog[exerciseName] || {};
      const entry = {
        rpe: existingEntry.rpe || "",
        sets: Array.isArray(existingEntry.sets) ? [...existingEntry.sets] : [],
      };

      if (field === "rpe") {
        entry.rpe = value;
      } else if (
        field === "setWeight" ||
        field === "setReps" ||
        field === "setRpe"
      ) {
        const idx =
          typeof setIndex === "number" && setIndex >= 0 ? setIndex : 0;
        const setsArr = [...entry.sets];
        while (setsArr.length <= idx)
          setsArr.push({ weight: "", reps: "", rpe: "" });

        if (field === "setWeight")
          setsArr[idx] = { ...setsArr[idx], weight: value };
        if (field === "setReps")
          setsArr[idx] = { ...setsArr[idx], reps: value };
        if (field === "setRpe") setsArr[idx] = { ...setsArr[idx], rpe: value };

        entry.sets = setsArr;
      }

      return {
        ...prev,
        [selectedHero.id]: {
          ...heroLog,
          [selectedWeek]: {
            ...weekLog,
            [dayId]: {
              ...dayLog,
              [exerciseName]: entry,
            },
          },
        },
      };
    });
  };

  const handleShowExerciseVideo = (exerciseName) => {
    if (!EXERCISE_VIDEO_MAP[exerciseName]) return;
    setSelectedExerciseForVideo(exerciseName);
    navigateToTab("library");
  };

  const handleSidekickCheckIn = (answer) => {
    setSidekickAnswered(true);
    try {
      const raw = window.localStorage.getItem(SIDEKICK_CHECKIN_KEY);
      const map = raw ? JSON.parse(raw) : {};
      map[todayStr] = answer;
      window.localStorage.setItem(SIDEKICK_CHECKIN_KEY, JSON.stringify(map));
    } catch {}
  };

  // ✅ Auto-scroll into grid when opening browse (NEW)
  useEffect(() => {
    if (activeTab !== "heroes") return;
    if (!heroBrowseOpen) return;
    try {
      heroBrowseTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } catch {}
  }, [heroBrowseOpen, activeTab]);

  // ✅ Close browse grid when leaving Heroes tab (keeps it clean)
  useEffect(() => {
    if (activeTab !== "heroes") setHeroBrowseOpen(false);
  }, [activeTab]);

  if (!authId) {
    return (
      <AuthCover
        firebaseReady={fbReady}
        onLogin={loginWithEmail}
        onSignup={signupWithEmail}
      />
    );
  }

  let content = null;

  if (activeTab === "heroes") {
    content = (
      <div className="card">
        <div className="card-header">
          <div className="card-title">Choose Your Hero</div>
          <div className="tag">Spotlight</div>
        </div>

        {isTrialLimited && (
          <div
            style={{
              marginTop: 6,
              marginBottom: 10,
              padding: "8px 10px",
              borderRadius: 12,
              border: "1px solid rgba(56,189,248,0.35)",
              background: "rgba(56,189,248,0.08)",
              fontSize: "0.85rem",
              color: "#e0f2fe",
            }}
          >
            Trial access: You can choose <strong>2 heroes</strong>. Unlock full
            access to select all heroes.
          </div>
        )}

        {/* ✅ Spotlight landing card (NEW) */}
        <HeroSpotlightLanding
          hero={selectedHero}
          program={program}
          weeksCount={weeksCount}
          daysPerWeek={daysPerWeek}
          onContinue={() => navigateToTab("workouts")}
          browseOpen={heroBrowseOpen}
          onToggleBrowse={() => setHeroBrowseOpen((v) => !v)}
          onViewProgress={() => navigateToTab("progress")}
          onViewChallenges={() => navigateToTab("challenges")}
        />

        {/* ✅ Browse grid (hidden until "Change Hero") */}
        {heroBrowseOpen && (
          <div ref={heroBrowseTopRef} style={{ marginTop: 12 }}>
            <UniverseTabs
              selectedUniverse={selectedUniverse}
              onSelect={handleUniverseChange}
              locked={isTrialLimited}
              onLockedClick={() =>
                openPaywall(
                  "Filters Locked 🔒",
                  "Universe & gender filters are premium. Upgrade to browse all heroes by universe."
                )
              }
            />

            <GenderFilterTabs
              genderFilter={genderFilter}
              onSelect={setGenderFilter}
              locked={isTrialLimited}
              onLockedClick={() =>
                openPaywall(
                  "Filters Locked 🔒",
                  "Universe & gender filters are premium. Upgrade to browse all heroes by universe."
                )
              }
            />

            <HeroGrid
              selectedUniverse={selectedUniverse}
              genderFilter={genderFilter}
              selectedHeroId={selectedHeroId}
              onSelectHero={handleHeroChangeSpotlight}
              onAutoGoWorkouts={
                null
              } /* ✅ Spotlight flow: user presses Continue Workouts */
              allowedHeroIds={isTrialLimited ? trialAllowedHeroIds : null}
              onLockedHeroClick={() =>
                openPaywall(
                  "Hero Locked 🔒",
                  "During the free trial, only 2 heroes are selectable. Unlock full access to choose any hero."
                )
              }
            />
          </div>
        )}
      </div>
    );
  } else if (activeTab === "workouts") {
    content = (
      <div className="card">
        <div className="card-header">
          <div className="card-title">Workouts ({weeksCount}-Week Block)</div>
          <div className="tag">
            {program.weeks}-week • {program.frequency}
          </div>
        </div>

        {!trialMeta.subscribed &&
          (trialMeta.inTrial || trialMeta.trialExpired) && (
            <div
              style={{
                marginTop: 6,
                marginBottom: 10,
                padding: "8px 10px",
                borderRadius: 12,
                border: "1px solid rgba(248,250,252,0.18)",
                background: "rgba(15,23,42,0.75)",
                fontSize: "0.85rem",
                color: "#e5e7eb",
              }}
            >
              {trialMeta.inTrial ? (
                <>
                  Trial access: <strong>Week 1 only</strong>. Unlock full access
                  to train Weeks 2–{weeksCount}.
                </>
              ) : (
                <>Trial ended: Subscribe to unlock Weeks 2–{weeksCount}.</>
              )}
            </div>
          )}

        <div className="hero-physique" style={{ marginBottom: 4 }}>
          <strong>{selectedHero.name}</strong> — {selectedHero.physique}
        </div>

        <div className="hero-meta" style={{ marginBottom: 10 }}>
          <span className="badge">{selectedHero.universe}</span>
          <span className="badge">
            {selectedHero.gender === "female" ? "Female Hero" : "Male Hero"}
          </span>
          <span className="badge">{program.name}</span>
        </div>

        <div className="freq-row">
          <div className="section-label">Program length</div>
          <div className="freq-toggle-group">
            {[9, 12].map((len) => (
              <button
                key={len}
                className={
                  weeksCount === len ? "freq-pill active" : "freq-pill"
                }
                onClick={() => {
                  if (
                    !trialMeta.subscribed &&
                    (trialMeta.inTrial || trialMeta.trialExpired) &&
                    len > 9
                  ) {
                    openPaywall(
                      "Locked 🔒",
                      "Premium program options are locked until you subscribe."
                    );
                    return;
                  }
                  handleBlockLengthChange(len);
                }}
              >
                {len}-week
              </button>
            ))}
          </div>
        </div>

        <div className="freq-row">
          <div className="section-label">Training frequency</div>
          <div className="freq-toggle-group">
            {[4, 5].map((n) => (
              <button
                key={n}
                className={n === daysPerWeek ? "freq-pill active" : "freq-pill"}
                onClick={() => handleDaysPerWeekChange(n)}
              >
                {n} days/wk
              </button>
            ))}
          </div>
        </div>

        <div className="freq-row">
          <div className="section-label">Difficulty</div>
          <div className="freq-toggle-group">
            {["beginner", "intermediate", "advanced"].map((mode) => (
              <button
                key={mode}
                className={
                  mode === difficulty ? "freq-pill active" : "freq-pill"
                }
                onClick={() => handleDifficultyChange(mode)}
              >
                {mode === "beginner"
                  ? "Beginner"
                  : mode === "advanced"
                  ? "Advanced"
                  : "Intermediate"}
              </button>
            ))}
          </div>
        </div>

        <div className="section-label">Select week</div>
        <div className="week-row">
          {Array.from({ length: weeksCount }).map((_, idx) => {
            const weekNum = idx + 1;
            const lockedWeek =
              !trialMeta.subscribed &&
              (trialMeta.inTrial || trialMeta.trialExpired) &&
              weekNum > TRIAL_MAX_WEEK;

            return (
              <button
                key={weekNum}
                className={
                  weekNum === selectedWeek ? "week-pill active" : "week-pill"
                }
                onClick={() => {
                  if (lockedWeek) {
                    openPaywall(
                      "Week Locked 🔒",
                      "Only Week 1 is available until you subscribe."
                    );
                    return;
                  }
                  setSelectedWeek(weekNum);
                }}
                style={
                  lockedWeek ? { opacity: 0.5, cursor: "pointer" } : undefined
                }
                title={lockedWeek ? "Locked until subscription" : "Select week"}
              >
                W{weekNum}
                {lockedWeek ? " 🔒" : ""}
              </button>
            );
          })}
        </div>

        <div className="layout" style={{ marginTop: 12 }}>
          <HeroPanel hero={selectedHero} program={program} />
          <ProgramPanel
            program={program}
            daysPerWeek={daysPerWeek}
            selectedDayId={selectedDayId}
            onSelectDay={handleDayChange}
            progressForWeek={progressForHeroWeek}
            onToggleDay={handleToggleDay}
            selectedWeek={selectedWeek}
            difficulty={difficulty}
            logForWeek={logForHeroWeek}
            prevWeekLog={prevWeekLog}
            onUpdateLog={handleUpdateLog}
            onShowVideo={handleShowExerciseVideo}
          />
        </div>
      </div>
    );
  } else if (activeTab === "progress") {
    content = (
      <ProgressOverview
        progress={progress}
        daysPerWeekByHero={daysPerWeekByHero}
        log={log}
        blockLengthByHero={blockLengthByHero}
      />
    );
  } else if (activeTab === "logbook") {
    content = (
      <Logbook
        hero={selectedHero}
        program={program}
        logForHero={heroLogAllWeeks}
      />
    );
  } else if (activeTab === "profile") {
    content = (
      <ProfilePanel
        profile={profile}
        setProfile={setProfile}
        photos={profilePhotos}
        setPhotos={setProfilePhotos}
      />
    );
  } else if (activeTab === "library") {
    content = (
      <LibraryPanel
        selectedExercise={selectedExerciseForVideo}
        onSelectExercise={setSelectedExerciseForVideo}
      />
    );
  } else if (activeTab === "challenges") {
    content = (
      <ChallengesPanel
        hero={selectedHero}
        progress={progress}
        daysPerWeekByHero={daysPerWeekByHero}
        blockLengthByHero={blockLengthByHero}
        todayStr={todayStr}
      />
    );
  }

  return (
    <div className="app">
      {/* ✅ Disclaimer ALWAYS on top */}
      <DisclaimerModal
        open={disclaimerOpen}
        onAccept={() => {
          try {
            window.localStorage.setItem(DISCLAIMER_ACK_KEY, "true");
          } catch {}
          setDisclaimerOpen(false);
        }}
      />

      {/* ✅ Everything below is “locked” until disclaimer accepted */}
      <div
        style={{
          pointerEvents: disclaimerOpen ? "none" : "auto",
          filter: disclaimerOpen ? "blur(2px)" : "none",
          opacity: disclaimerOpen ? 0.85 : 1,
          transition: "filter 200ms ease, opacity 200ms ease",
        }}
      >
        <PaywallModal
          open={paywallOpen}
          onClose={() => setPaywallOpen(false)}
          title={paywallTitle}
          message={paywallMessage}
          checkoutUrl={CHECKOUT_URL}
          manageUrl={MANAGE_SUBSCRIPTION_URL}
        />

        <SettingsPanel
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          authId={authId}
          subscriptionLabel={subscriptionLabel}
          manageSubscriptionUrl={MANAGE_SUBSCRIPTION_URL}
          checkoutUrl={CHECKOUT_URL}
          hasFullAccess={hasFullAccess}
          onLogout={async () => {
            await handleLogout();
            setSettingsOpen(false);
          }}
        />

        <NavToggle activeTab={activeTab} onChange={navigateToTab} />

        {tabHistory.length > 0 && (
          <button
            onClick={goBack}
            style={{
              position: "fixed",
              top: 10,
              left: 10,
              zIndex: 60,
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,0.5)",
              background: "rgba(15,23,42,0.95)",
              color: "#e5e7eb",
              padding: "4px 10px",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
            title="Go back"
          >
            ← Back
          </button>
        )}

        <button
          onClick={() => setSettingsOpen(true)}
          style={{
            position: "fixed",
            top: 10,
            right: 10,
            zIndex: 60,
            borderRadius: 999,
            border: "1px solid rgba(148,163,184,0.5)",
            background: "rgba(15,23,42,0.95)",
            color: "#e5e7eb",
            padding: "4px 8px",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
          title="Settings"
        >
          ⚙️
        </button>

        <h1 className="app-title">
          Hero Physique
          <span>
            Pick a hero. Follow their 9–12 week program. 4–5 days per week with
            progressive overload, exercise variants that scale with your level,
            per-set logging, advanced-only RPE, tracking, logbook, profile, and
            momentum tracking.
          </span>
        </h1>

        {content}

        {sidekickMessage && (
          <div
            style={{
              position: "fixed",
              bottom: 70,
              right: 10,
              zIndex: 55,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 8,
            }}
          >
            {sidekickOpen && (
              <div
                className="card"
                style={{
                  maxWidth: 320,
                  padding: 12,
                  boxShadow: "0 10px 40px rgba(15,23,42,0.95)",
                  background: "rgba(2,6,23,0.98)",
                  border: "1px solid rgba(148,163,184,0.45)",
                }}
              >
                <div
                  style={{
                    fontSize: "0.75rem",
                    opacity: 0.7,
                    marginBottom: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>🤖</span>
                  <span>Daily Sidekick Check-In</span>
                </div>
                <div
                  style={{
                    fontSize: "0.95rem",
                    marginBottom: 8,
                    lineHeight: 1.4,
                  }}
                >
                  “{sidekickMessage}”
                </div>

                {!sidekickAnswered ? (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 6,
                      fontSize: "0.8rem",
                    }}
                  >
                    <span>Did you train today?</span>
                    <button
                      onClick={() => handleSidekickCheckIn("yes")}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(34,197,94,0.6)",
                        background: "rgba(22,163,74,0.15)",
                        color: "#bbf7d0",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      Yes, Sidekick ✅
                    </button>
                    <button
                      onClick={() => handleSidekickCheckIn("not-yet")}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(248,250,252,0.2)",
                        background: "rgba(15,23,42,0.7)",
                        color: "#e5e7eb",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      Not yet – I will 💪
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "0.8rem",
                      opacity: 0.9,
                      marginBottom: 6,
                    }}
                  >
                    Proud of you for checking in today. Keep stacking days. 🧱
                  </div>
                )}

                <button
                  onClick={() => setSidekickOpen(false)}
                  style={{
                    marginTop: 4,
                    fontSize: "0.75rem",
                    padding: "3px 8px",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.6)",
                    background: "transparent",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    float: "right",
                  }}
                >
                  Close
                </button>
              </div>
            )}

            <button
              onClick={() => setSidekickOpen((open) => !open)}
              style={{
                width: 44,
                height: 44,
                borderRadius: "999px",
                border: sidekickOpen
                  ? "2px solid rgba(94,234,212,0.9)"
                  : "1px solid rgba(148,163,184,0.7)",
                background: sidekickOpen
                  ? "radial-gradient(circle at 30% 0%, #22d3ee 0, #0f172a 55%)"
                  : "rgba(15,23,42,0.95)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 8px 30px rgba(15,23,42,0.9)",
              }}
              title="Hero Sidekick"
            >
              <span style={{ fontSize: "1.2rem" }}>🤖</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
